/******/ (() => { // webpackBootstrap
/*!**************************************!*\
  !*** ./js/sections/welcome-popup.js ***!
  \**************************************/
/**
 * Welcome Popup - AdaptHealth Diabetes
 *
 * Supports 3 types:
 * 1. General popup - shows for all visitors
 * 2. Referral/condition-based popup - shows only when URL has matching query param
 * 3. Domain change (exit interstitial) - intercepts clicks on links to watched domains
 *    and shows a popup before the user leaves the site
 *
 * Priority: Referral popups always take priority over general popups.
 * Up to 10 popups can be configured via Shopify section blocks.
 *
 * Mobile: Slides up from bottom (bottom sheet style).
 * Desktop: Centered modal with fade-in + scale animation.
 */

var MOBILE_BREAKPOINT = 768;
function normalizeHost(h) {
  return (h || '').replace(/^www\./, '').toLowerCase();
}
function parseDomains(raw) {
  return (raw || '').split(/[,\n\r]+/).map(d => normalizeHost(d.trim())).filter(Boolean);
}
var REPEAT_INTERVALS = {
  every_30_mins: 30 * 60 * 1000,
  every_1_hr: 60 * 60 * 1000,
  every_6_hrs: 6 * 60 * 60 * 1000,
  every_12_hrs: 12 * 60 * 60 * 1000,
  every_day: 24 * 60 * 60 * 1000,
  every_3_days: 3 * 24 * 60 * 60 * 1000,
  every_week: 7 * 24 * 60 * 60 * 1000,
  every_2_weeks: 14 * 24 * 60 * 60 * 1000,
  every_month: 30 * 24 * 60 * 60 * 1000
};

// Track which popup is currently active (only one at a time)
var activePopup = null;
class WelcomePopup extends HTMLElement {
  constructor() {
    super();
    this.popupId = this.id;
    this.type = this.dataset.popupType || 'general'; // 'general', 'referral', or 'domain_change'
    this.queryString = (this.dataset.queryString || '').trim(); // e.g. "?referrer=onehome"
    this.domainList = (this.dataset.domainList || '').trim(); // comma-separated domains
    this.triggerOpen = this.dataset.triggerOpen || 'immediately';
    this.repeatOpen = this.dataset.repeatOpen || 'no_repeat';
    this.isOpen = false;
    this.pendingUrl = null; // destination URL when intercepting outbound clicks
  }
  connectedCallback() {
    this.closeBtn = this.querySelector('.welcome-popup__close');
    this.overlay = this.querySelector('.welcome-popup__overlay');
    this.dialog = this.querySelector('.welcome-popup__dialog');
    this.bindEvents();
    this.setMobileClass();
  }
  disconnectedCallback() {
    this.unbindEvents();
  }

  // --- Mobile Detection ---

  isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }
  setMobileClass() {
    if (this.isMobile()) {
      this.classList.add('welcome-popup--mobile');
      this.classList.remove('welcome-popup--desktop');
    } else {
      this.classList.add('welcome-popup--desktop');
      this.classList.remove('welcome-popup--mobile');
    }
  }

  // --- Visibility Logic ---

  shouldShow() {
    // Shopify design mode always shows
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return true;

    // Check if popup is enabled
    if (this.dataset.enable !== 'true') return false;

    // Type-based check
    if (this.type === 'referral') {
      // Referral: only check query param match, no trigger/repeat logic
      return this.matchesQueryParam();
    }
    if (this.type === 'domain_change') {
      // Domain change popups are triggered by outbound link clicks, not on page load
      return false;
    }

    // General: apply repeat-open check (localStorage)
    if (!this.canRepeatOpen()) return false;
    return true;
  }

  /** Check if this domain_change popup is enabled and has domains configured */
  isOutboundInterceptEnabled() {
    if (this.dataset.enable !== 'true') return false;
    if (this.type !== 'domain_change') return false;
    return !!this.domainList;
  }
  matchesQueryParam() {
    if (!this.queryString) return false;

    // Parse the configured query string (e.g. "?referrer=onehome" or "referrer=onehome")
    var configuredParams = new URLSearchParams(this.queryString.startsWith('?') ? this.queryString : '?' + this.queryString);
    var currentParams = new URLSearchParams(window.location.search);

    // Every param in the configured string must match the current URL
    for (var [key, value] of configuredParams.entries()) {
      if (currentParams.get(key) !== value) return false;
    }
    return true;
  }

  /** Open the popup as an exit interstitial, storing the destination URL */
  openWithDestination(destUrl) {
    this.pendingUrl = destUrl;

    // Set ALL visible buttons to redirect to the destination URL
    var buttons = this.querySelectorAll('.welcome-popup__btn a');
    buttons.forEach(btn => {
      btn.href = destUrl;
      btn.setAttribute('target', '_blank');
      btn.setAttribute('rel', 'noopener noreferrer');
      if (this._cancelHandler) btn.removeEventListener('click', this._cancelHandler);
      btn.addEventListener('click', () => {
        this.close();
      }, {
        once: true
      });
    });
    this.open();
  }
  canRepeatOpen() {
    // Always show on every page load
    if (this.repeatOpen === 'always') return true;
    var storageKey = "welcome-popup-".concat(this.popupId, "-last-shown");
    var lastShown = localStorage.getItem(storageKey);
    if (!lastShown) return true;
    if (this.repeatOpen === 'no_repeat') return false;
    var elapsed = Date.now() - parseInt(lastShown, 10);
    var interval = REPEAT_INTERVALS[this.repeatOpen];
    return interval ? elapsed >= interval : true;
  }
  markAsShown() {
    var storageKey = "welcome-popup-".concat(this.popupId, "-last-shown");
    localStorage.setItem(storageKey, Date.now().toString());
  }

  // --- Trigger Logic ---

  scheduleOpen() {
    // Referral popups always open immediately
    if (this.type === 'referral') {
      this.open();
      return;
    }
    switch (this.triggerOpen) {
      case 'immediately':
        this.open();
        break;
      case 'delay':
        this._delayTimer = setTimeout(() => this.open(), 5000);
        break;
      case 'scroll_down':
        this._scrollHandler = () => {
          var scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight) * 100;
          if (scrollPercent >= 50) {
            this.open();
            window.removeEventListener('scroll', this._scrollHandler);
          }
        };
        window.addEventListener('scroll', this._scrollHandler, {
          passive: true
        });
        break;
      default:
        this.open();
    }
  }

  // --- Open / Close ---

  open() {
    if (this.isOpen) return;

    // Close any other active popup first
    if (activePopup && activePopup !== this) {
      activePopup.close();
    }
    activePopup = this;
    this.isOpen = true;
    this.setMobileClass();
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    document.body.classList.add('onehome-backdrop');

    // Double rAF ensures browser paints the unhidden state before adding animation class
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('welcome-popup--visible');
      });
    });
    this.markAsShown();

    // Trap focus
    var focusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) focusable.focus();
  }
  close() {
    if (!this.isOpen) return;
    this.classList.remove('welcome-popup--visible');
    this.pendingUrl = null;

    // Wait for CSS transition to finish before hiding
    var onTransitionEnd = () => {
      this.isOpen = false;
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('onehome-backdrop');
      if (activePopup === this) activePopup = null;
      this.removeEventListener('transitionend', onTransitionEnd);
    };
    this.addEventListener('transitionend', onTransitionEnd);

    // Fallback in case transitionend doesn't fire
    setTimeout(() => {
      if (this.isOpen) onTransitionEnd();
    }, 500);
  }

  // --- Event Binding ---

  bindEvents() {
    if (this.closeBtn) {
      this._onClose = () => this.close();
      this.closeBtn.addEventListener('click', this._onClose);
    }
    if (this.overlay) {
      this._onOverlayClick = () => this.close();
      this.overlay.addEventListener('click', this._onOverlayClick);
    }
    this._onKeydown = e => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this._onKeydown);

    // Update mobile/desktop class on resize
    this._onResize = () => {
      if (this.isOpen) this.setMobileClass();
    };
    window.addEventListener('resize', this._onResize, {
      passive: true
    });
  }
  unbindEvents() {
    if (this._onClose && this.closeBtn) this.closeBtn.removeEventListener('click', this._onClose);
    if (this._onOverlayClick && this.overlay) this.overlay.removeEventListener('click', this._onOverlayClick);
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._delayTimer) clearTimeout(this._delayTimer);
    if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
  }
}

// Register custom element
if (!customElements.get('welcome-popup')) {
  customElements.define('welcome-popup', WelcomePopup);
}

/**
 * Initialize popups with priority logic:
 * 1. Collect all welcome-popup elements
 * 2. Check referral popups first (higher priority)
 * 3. If no referral popup matches, check general popups
 * 4. Only one popup opens at a time
 * domain_change popups are NOT opened on page load — they are triggered by outbound clicks.
 */
function initWelcomePopups() {
  var allPopups = Array.from(document.querySelectorAll('welcome-popup'));
  if (!allPopups.length) return;

  // Split into referral and general
  var referralPopups = allPopups.filter(p => p.type === 'referral');
  var generalPopups = allPopups.filter(p => p.type === 'general');

  // Try referral popups first (priority)
  for (var popup of referralPopups) {
    if (popup.shouldShow()) {
      popup.scheduleOpen();
      return; // Only one popup at a time
    }
  }

  // No referral popup matched — try general popups
  for (var _popup of generalPopups) {
    if (_popup.shouldShow()) {
      _popup.scheduleOpen();
      return; // Only one popup at a time
    }
  }
}

/**
 * Intercept clicks on links to watched domains and show the popup before leaving.
 * Also detects internal links that Shopify redirects to watched external domains.
 */
function interceptOutboundLinks() {
  var host = normalizeHost(location.hostname);

  // Collect all enabled domain_change popups and their watched domains
  var domainPopups = [];
  document.querySelectorAll('welcome-popup[data-popup-type="domain_change"]').forEach(el => {
    if (!el.isOutboundInterceptEnabled()) return;
    var domains = parseDomains(el.dataset.domainList || '');
    // Only watch domains that are different from the current host
    var externalDomains = domains.filter(d => d !== host);
    if (externalDomains.length) {
      domainPopups.push({
        popup: el,
        domains: externalDomains
      });
    }
  });
  if (!domainPopups.length) return;

  // Cache redirect check results: pathname -> { type: 'internal' } | { type: 'external', url, popup }
  var redirectCache = {};

  // Find the matching popup for a given external hostname
  function findPopupForHost(h) {
    for (var i = 0; i < domainPopups.length; i++) {
      if (domainPopups[i].domains.indexOf(h) !== -1) return domainPopups[i];
    }
    return null;
  }
  document.addEventListener('click', e => {
    var a = e.target.closest('a[href]');
    if (!a) return;
    // Skip links inside the popup itself (e.g. "Continue to site" button)
    if (a.closest('welcome-popup')) return;
    try {
      var u = new URL(a.href, location.origin);
      var h = normalizeHost(u.hostname);

      // --- External links: instant interception ---
      if (h !== host) {
        var match = findPopupForHost(h);
        if (match) {
          e.preventDefault();
          e.stopPropagation();
          match.popup.openWithDestination(u.toString());
        }
        return;
      }

      // --- Internal links: detect Shopify redirects to watched external domains ---
      var pathname = u.pathname.toLowerCase();

      // Skip same-page anchor links
      if (pathname === location.pathname.toLowerCase() && u.hash) return;

      // Check cache
      var cached = redirectCache[pathname];
      if (cached) {
        if (cached.type === 'internal') return; // known non-redirect, navigate normally
        // Known external redirect — show popup
        e.preventDefault();
        e.stopPropagation();
        cached.popup.popup.openWithDestination(cached.url);
        return;
      }

      // Not cached — fetch to detect redirect
      e.preventDefault();
      e.stopPropagation();
      fetch(a.href, {
        method: 'HEAD',
        redirect: 'follow'
      }).then(response => {
        var finalHost = normalizeHost(new URL(response.url).hostname);
        if (finalHost !== host) {
          // Redirected to an external domain
          var _match = findPopupForHost(finalHost);
          if (_match) {
            redirectCache[pathname] = {
              type: 'external',
              url: response.url,
              popup: _match
            };
            _match.popup.openWithDestination(response.url);
            return;
          }
        }
        // Not a redirect or not a watched domain — navigate normally
        redirectCache[pathname] = {
          type: 'internal'
        };
        window.location.href = a.href;
      }).catch(() => {
        // CORS error means it redirected to an external domain
        // Show popup with original URL (Shopify will redirect when user clicks Continue)
        redirectCache[pathname] = {
          type: 'external',
          url: a.href,
          popup: domainPopups[0]
        };
        domainPopups[0].popup.openWithDestination(a.href);
      });
    } catch (err) {}
  }, true);
}

// Boot
function boot() {
  initWelcomePopups();
  interceptOutboundLinks();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
/******/ })()
;